import React, { useEffect } from 'react';
import {
    useAppDispatch,
    useAppSelector,
    useTheme,
    PargodaRoundButton,
    Box,
} from '../../../../base';
import { toolsActions, toolsSelectors } from '../../../../actions';
import {
    CompanyQuestionType,
    ICompanyQuestion,
    IQuestionAnswer,
} from '../../../../common';
import { useState } from 'react';
import {
    UnifiedAnswerType,
    AllAnswerOwnProps,
} from '../../AnswerActionComponents/AnswerActionComponents.interfaces';
import {
    BottomResponseAreaWrapper,
    SendButtonContainer,
} from '../../../ToolsPageLayout/ToolsPageLayout.styles';
import { useAnswerComponentConfig } from '../../../../hooks/useAnswerComponentConfig';
import { useTranslation } from 'react-i18next';
import PargodaLoading from '../../../PargodaLoading';

const ChatInput = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const activeChatId = useAppSelector(toolsSelectors.getActiveChatId);
    const activeCompany = useAppSelector(toolsSelectors.getActiveCompany);
    const activeChatHistory = useAppSelector(
        toolsSelectors.getActiveChatHistory
    );
    const [currentQuestion, setCurrentQuestion] =
        useState<ICompanyQuestion | null>(null);
    const [inputValue, setInputValue] = useState<string>('');
    const [isDefaultInput, setIsDefaultInput] = useState(false);
    const loading = false;

    useEffect(() => {
        const lastMessage =
            activeChatHistory.chatHistory.length > 0
                ? activeChatHistory.chatHistory[
                      activeChatHistory.chatHistory.length - 1
                  ]
                : null;

        const questionTemplate = lastMessage?.toolChatQuestionTemplate;

        if (questionTemplate) {
            setCurrentQuestion(questionTemplate);
            setIsDefaultInput(false);
        } else {
            setCurrentQuestion({
                type: CompanyQuestionType.FREE_TEXT,
                id: 0,
                title: '',
                options: [],
                category: '',
                index: 0,
                weight: 0,
            });
            setIsDefaultInput(true);
        }
    }, [activeChatHistory]);

    const answerComponentConfig = useAnswerComponentConfig(currentQuestion);

    const handleSendMessage = () => {
        if (!inputValue.trim()) return;
        const answerObject: IQuestionAnswer = {
            answer: inputValue,
        };
        if (activeChatId) {
            dispatch(
                toolsActions.sendChatMessage({
                    chatId: activeChatId,
                    answerObject,
                })
            );
        } else {
            dispatch(
                toolsActions.sendCreateChatMessage({
                    companyId: activeCompany?.value || '',
                    answerObject,
                })
            );
        }
        setInputValue('');
    };

    const handleInputChange = (value: UnifiedAnswerType) => {
        if (value === null || value === undefined) {
            setInputValue('');
        } else if (typeof value === 'string') {
            setInputValue(value);
        } else if (typeof value === 'boolean') {
            setInputValue(String(value));
        } else if (Array.isArray(value)) {
            setInputValue(value.join(', '));
        } else if (typeof value === 'object' && 'label' in value) {
            setInputValue(value.label);
        } else if (value instanceof File) {
            setInputValue(value.name);
        } else {
            console.warn('Unhandled answer type in ChatInput:', value);
        }
    };

    const enhancedProps: AllAnswerOwnProps & {
        value: string;
        onSelectionChange: (value: UnifiedAnswerType) => void;
        onChange?: (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => void;
        onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
        placeholder?: string;
        multiline?: boolean;
        rows?: number;
        fullWidth?: boolean;
        variant?: 'outlined' | 'filled' | 'standard';
        disabled?: boolean;
    } = {
        ...((answerComponentConfig?.props as AllAnswerOwnProps) || {}),
        value: inputValue,
        onSelectionChange: handleInputChange,
        disabled: isDefaultInput && activeChatHistory.chatHistory.length !== 0,
    };

    if (
        currentQuestion?.type === CompanyQuestionType.FREE_TEXT &&
        answerComponentConfig &&
        answerComponentConfig.Component
    ) {
        enhancedProps.onChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => handleInputChange(e.target.value);
        enhancedProps.onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
                e.preventDefault();
                handleSendMessage();
            }
        };
        enhancedProps.placeholder = t('tools.chat.textBoxPlaceholder');
        enhancedProps.multiline = true;
        enhancedProps.rows = 4;
        enhancedProps.fullWidth = true;
        enhancedProps.variant = 'outlined';
    }

    return (
        <BottomResponseAreaWrapper>
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        borderRadius: 'inherit', // Or specific value
                    }}
                >
                    <PargodaLoading height={200} width={200} />
                </Box>
            )}
            {answerComponentConfig && answerComponentConfig.Component && (
                <answerComponentConfig.Component
                    {...(enhancedProps as AllAnswerOwnProps)}
                />
                /* Using "as any" as enhancedProps are dynamically built for different components.
                   Type safety for the specific component (TextAction here) is handled by the conditional logic above. */
            )}
            <SendButtonContainer>
                <PargodaRoundButton
                    variant="contained"
                    color="primary"
                    disabled={
                        !inputValue.trim() ||
                        (isDefaultInput &&
                            activeChatHistory.chatHistory.length !== 0)
                    }
                    sx={{
                        width: '210px',
                        background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': {
                            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 86.24%)`,
                        },
                    }}
                    onClick={handleSendMessage}
                >
                    {t('tools.chat.send')}
                </PargodaRoundButton>
            </SendButtonContainer>
        </BottomResponseAreaWrapper>
    );
};

export default ChatInput;
